import { type ProductType, type UserResource } from "@exonn/js-sdk";
import { type TabOptions } from "src/lib/tabs-ui/TabsStore.tsx";

import React, { useEffect, useMemo, useState } from "react";
import { useDataRouterContext } from "src/lib/react-router/useDataRouterContext.ts";

import { type TabDefinition } from "src/lib/tabs/types.ts";
import { whenRoutePathIs } from "src/lib/tabs/whenRoutePathIs.ts";
import { TranslationNamespaceEnum } from "src/lib/i18next/types.ts";
import { Outlet } from "react-router";
import { theEnd } from "src/lib/tabs/theEnd.ts";

import { theBeginning } from "src/lib/tabs/theBeginning.ts";
import { Tabs } from "src/components/Tabs/Tabs.tsx";
import { useResourceStorageKeys } from "src/framework/useResourceStorageKeys.tsx";
import { memoryStorageDriver } from "src/storage/memoryStorage.driver.ts";
import { useRouterTabsV2 } from "src/lib/tabs/useRouterTabsV2.tsx";
import { useEditTabsV2 } from "src/framework/layoutV2/useEditTabsV2.tsx";
import { EditTabTitleV2 } from "src/framework/layoutV2/EditTabTitleV2.tsx";

import { observer } from "mobx-react-lite";
import {
  ModuleTabsProvider,
  ModuleTabsStore,
} from "src/framework/layout/ModuleTabsProvider.tsx";
import { useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { useModuleRoutePaths } from "src/system/useModuleRoutePaths.ts";
import { useClientConfig } from "src/client-config/useClientConfig.ts";
import { ResourceEnum } from "src/constants/constants.ts";
import { SelectProductType } from "src/admin/modals/product/SelectProductType/SelectProductType.tsx";
import { useBoolean } from "src/hooks/use-boolean.ts";
import { createModuleRoutePaths } from "src/system/createModuleRoutePaths.ts";
import { useUserDataState } from "src/user-data/useUserDataState.ts";
import { ModuleLayoutTabsUserDataSchema } from "src/user-data/schemas.ts";
import type { ModuleTabsPersistedState } from "src/user-data/types.ts";
import { sanitazeModuleLayoutTabs } from "src/user-data/sanitazors.ts";
import { clearCreateResourceFormState } from "src/system/clearCreateResourceFormState.ts";
import { clearEditResourceFormState } from "src/system/clearEditResourceFormState.ts";
import { AppVariation } from "src/client-config/getClientConfig.ts";
import { ProductCreateFormSearchParams } from "src/admin/modules/product/constants.ts";
import { matchRoutes } from "react-router-dom";
import { moduleTranslationsQuery } from "src/system/moduleTranslationsQuery.tsx";
import { useModuleCreateDisabled } from "src/system/useModuleCreateDisabled.ts";
import { useModule } from "src/system/useModule.tsx";
import { getResourceI18nNamespace } from "src/system/getResourceI18nNamespace.ts";
import { useSubscribeToUnsavedChanges } from "src/framework/layoutV2/useSubscribeTabsToFormUnsavedChanges.ts";
import { useErpTranslation } from "src/system/useErpTranslation.ts";

export const ModuleLayoutTabsV2 = observer(
  (props: { user: UserResource; resource: string }) => {
    const { user, resource } = props;

    const clientConfig = useClientConfig();

    useSuspenseQuery(
      moduleTranslationsQuery({
        resource,
      }),
    );

    const {
      value: selectProductTypeModalOpen,
      setFalse: closeSelectProductTypeModal,
      setTrue: openSelectProductModal,
    } = useBoolean(false);

    const { router } = useDataRouterContext();
    const storageKeys = useResourceStorageKeys({
      resource: resource,
    });
    const queryClient = useQueryClient();
    const { formTabs: formTabsConfig } = useModule(resource);

    const routePaths = useModuleRoutePaths({
      resource: resource,
    });

    const module = useModule(resource);
    const tabsConfig = module.tabs;
    const hideTabPlusButton = tabsConfig?.hidePlusButton;

    const disableCreate = useModuleCreateDisabled(resource);

    const editIdParam = "id"; // todo: move to global settings

    const { t } = useErpTranslation({
      resource,
      type: "layout",
    });

    const storageKey = storageKeys.tabs;

    const create = useMemo(() => {
      if (resource === ResourceEnum.Stock) {
        const moduleRoutes = createModuleRoutePaths({
          resource: ResourceEnum.StockMove,
        });

        return {
          path: moduleRoutes.create.root,
        };
      }
      return {
        path: routePaths.create.root,
      };
    }, [routePaths, resource]);

    const edit = useMemo(() => {
      return {
        path: routePaths.edit.root,
        ...tabsConfig?.edit,
      };
    }, [routePaths, tabsConfig?.edit]);

    const list = useMemo(
      () => ({
        path: routePaths.root,
      }),
      [routePaths],
    );

    const { value: userDataState, setValue: setUserDataState } =
      useUserDataState<ModuleTabsPersistedState>({
        key: storageKey,
        validationSchema: ModuleLayoutTabsUserDataSchema,
        sanitaze: sanitazeModuleLayoutTabs(router),
        user,
        defaultValue: {
          paths: [list.path],
        },
      });

    const [moduleTabsStore] = useState(() => {
      return new ModuleTabsStore({
        paths: userDataState.paths,
        editPath: edit?.path,
        createPath: create?.path,
        router,
      });
    });

    const config: TabDefinition<TabOptions>[] = useMemo(() => {
      const items: TabDefinition<TabOptions>[] = [];
      if (create) {
        items.push({
          shouldOpen: whenRoutePathIs(create.path),
          mapToUiModel: (key) => {
            const storageKey = storageKeys.form({ type: "create" });
            const hasUnsavedChanges = !!memoryStorageDriver.get(storageKey);
            const title = t("new", {
              ns: TranslationNamespaceEnum.Common,
            });

            const handleClose = () => {
              const hasUnsavedChanges = !!memoryStorageDriver.get(storageKey);
              if (
                hasUnsavedChanges &&
                !window.confirm(
                  t("tabUnsavedChangesWarning", {
                    ns: TranslationNamespaceEnum.FormComponent,
                  }),
                )
              ) {
                return true; // prevents closing
              }
              clearCreateResourceFormState({
                resource,
                queryClient,
              });
            };
            return {
              id: key,
              hasUnsavedChanges,
              onClose: handleClose,
              title,
              isClosable: true,
              content: <Outlet />,
            };
          },
          insertAt: theEnd,
        });
      }

      if (edit) {
        const insertAt = moduleTabsStore.isCreateTabOpen
          ? (tabs: string[]) => tabs.length - 1
          : theEnd;
        items.push({
          shouldOpen: whenRoutePathIs(edit.path),
          mapToUiModel: (key, match) => {
            const currentId = match.params[editIdParam]!;

            const hasUnsavedChanges = Object.keys(
              formTabsConfig || {
                general: {},
              },
            ).some((key) => {
              return !!memoryStorageDriver.get(
                storageKeys.form({
                  id: currentId,
                  type: "edit",
                  tab: key,
                }),
              );
            });
            const handleClose = () => {
              const hasUnsavedChanges = Object.keys(
                formTabsConfig || {
                  general: {},
                },
              ).some((key) => {
                return !!memoryStorageDriver.get(
                  storageKeys.form({
                    id: currentId,
                    type: "edit",
                    tab: key,
                  }),
                );
              });
              if (
                hasUnsavedChanges &&
                !window.confirm(
                  t("tabUnsavedChangesWarning", {
                    ns: TranslationNamespaceEnum.FormComponent,
                  }),
                )
              ) {
                return true; // prevents closing
              }
              clearEditResourceFormState({
                resource,
                id: currentId,
                queryClient,
              });
            };

            const title = (
              <EditTabTitleV2
                getIdFromRow={edit?.getId}
                getTitle={edit?.getTitle}
                includes={edit?.includes}
                idParam={editIdParam}
                resource={resource}
                id={currentId}
              />
            );
            return {
              id: key,
              hasUnsavedChanges,
              onClose: handleClose,
              title,
              maxWidth: 200,
              minWidth: 40,
              isClosable: true,
              content: <Outlet />,
            };
          },
          insertAt: insertAt,
        });
      }

      items.push({
        shouldOpen: whenRoutePathIs(list.path),
        mapToUiModel: (key) => {
          const title = t(`all`, {
            ns: getResourceI18nNamespace(resource),
          });
          return {
            id: key,
            title,
            isClosable: false,
            content: <Outlet />,
          };
        },
        insertAt: theBeginning,
      });

      return items;
    }, [
      create,
      edit,
      list,
      t,
      formTabsConfig,
      editIdParam,
      storageKeys,
      moduleTabsStore.isCreateTabOpen,
      resource,
      queryClient,
    ]);

    useEditTabsV2({
      moduleTabsStore,
      resource,
      idParam: editIdParam,
      includes: edit?.includes,
    });

    const {
      tabs,
      activeTabId: activeTabKey,
      onTabsChange: setTabs,
      onActiveTabIdChange: setActiveTabKey,
      activePath,
    } = useRouterTabsV2({
      paths: moduleTabsStore.paths,
      router,
      onPathsChange: moduleTabsStore.setPaths,
      config,
    });

    useSubscribeToUnsavedChanges({
      moduleTabsStore,
      resource,
      formTabsConfig,
    });

    const pinnedLeft = tabs[0] ? [tabs[0].id] : [];

    useEffect(() => {
      setUserDataState({
        paths: moduleTabsStore.paths,
      });
    }, [storageKey, moduleTabsStore.paths, setUserDataState]);

    const hidePlusButton = disableCreate || hideTabPlusButton;

    const handlePlusButtonClick = () => {
      if (
        clientConfig.appVariation === AppVariation.B2bGateSupplier &&
        resource === ResourceEnum.Product
      ) {
        moduleTabsStore.openCreateTab();
      } else if (
        !moduleTabsStore.isCreateTabOpen &&
        resource === ResourceEnum.Product
      ) {
        openSelectProductModal();
      } else {
        moduleTabsStore.openCreateTab();
      }
    };

    const plusButtonHelperText = t(`new`, "New record", {
      ns: getResourceI18nNamespace(resource),
    });

    const handleSelectProductType = (type: ProductType) => {
      const searchParams = new URLSearchParams();
      searchParams.set(ProductCreateFormSearchParams.InitialProductType, type);
      router.navigate({
        pathname: moduleTabsStore.createPath,
        search: searchParams.toString(),
      });
    };

    const isCreateTabActive = () => {
      if (moduleTabsStore.createPath) {
        const matches = matchRoutes(
          [
            {
              path: moduleTabsStore.createPath,
            },
          ],
          activePath,
        );

        return (matches || []).length > 0;
      }

      return false;
    };

    return (
      <ModuleTabsProvider store={moduleTabsStore}>
        {selectProductTypeModalOpen && (
          <SelectProductType
            onClose={closeSelectProductTypeModal}
            onSelect={handleSelectProductType}
          />
        )}

        <Tabs
          pinnedLeft={pinnedLeft}
          hidePlusButton={hidePlusButton}
          disablePlusButton={isCreateTabActive()}
          onPlusButtonClick={handlePlusButtonClick}
          plusButtonHelperText={plusButtonHelperText}
          hasControlledActiveTabId
          activeTabId={activeTabKey}
          onActiveTabIdChange={setActiveTabKey}
          tabs={tabs}
          onTabsChange={setTabs}
          store={moduleTabsStore.baseStore}
        />
      </ModuleTabsProvider>
    );
  },
);
